import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../services/api";

const UsersGridLayer = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayCount, setDisplayCount] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [plans, setPlans] = useState([]);
  const [blockLoading, setBlockLoading] = useState({});
  const navigate = useNavigate();

  // Subscription modal states
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Generate month options
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Fetch plans for subscription filter
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.plans.getAll();
        if (response.success) {
          setPlans(response.data);
        } else {
          console.error("Failed to fetch plans:", response.message);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
  }, []);

  // Fetch users data from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await api.users.getAll();

        if (response.success) {
          setUsers(response.data);
        } else {
          setError(response.message || "Failed to fetch users");
        }
      } catch (err) {
        setError("Error connecting to server. Please try again later.");
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await api.users.delete(userId);

        if (response.success) {
          // Remove user from state
          setUsers(users.filter((user) => user.id !== userId));
          alert("User deleted successfully");
        } else {
          alert(response.message || "Failed to delete user");
        }
      } catch (err) {
        alert("Error deleting user. Please try again.");
        console.error("Error deleting user:", err);
      }
    }
  };

  // Handle view profile
  const handleViewProfile = (userId) => {
    navigate(`/view-profile`, { state: { userId } });
  };

  // Handle buying subscription for user
  const handleBuySubscription = (user) => {
    setSelectedUser(user);
    setSelectedPlan("");
    setShowSubscriptionModal(true);
  };

  // Handle subscription purchase
  const handleSubscriptionPurchase = async () => {
    if (!selectedPlan || !selectedUser) {
      alert("Please select a subscription plan");
      return;
    }

    setSubscriptionLoading(true);

    try {
      // Get the selected plan details
      const planDetails = plans.find((plan) => plan.id === selectedPlan);

      // Calculate start and end dates
      const startDate = new Date();
      const endDate = new Date(startDate);

      // Calculate end date based on plan duration
      if (planDetails.durationType === "WEEK") {
        endDate.setDate(endDate.getDate() + planDetails.duration * 7);
      } else {
        // Default to MONTH
        endDate.setMonth(endDate.getMonth() + planDetails.duration);
      }

      // Create subscription data
      const subscriptionData = {
        userId: selectedUser.id,
        planId: selectedPlan,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        isActive: true,
        autoRenew: false,
      };

      const response = await api.subscriptions.createSubscription(
        subscriptionData
      );

      if (response.success) {
        // Update user in the state with new subscription data
        setUsers(
          users.map((user) => {
            if (user.id === selectedUser.id) {
              return {
                ...user,
                subscription: response.data,
              };
            }
            return user;
          })
        );

        // Hide modal and reset states
        setShowSubscriptionModal(false);
        setSelectedUser(null);
        setSelectedPlan("");

        alert("Subscription purchased successfully");
      } else {
        alert(response.message || "Failed to purchase subscription");
      }
    } catch (error) {
      console.error("Error purchasing subscription:", error);
      alert("An error occurred while purchasing the subscription");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Find selected plan details
  const getSelectedPlanDetails = () => {
    if (!selectedPlan) return null;
    return plans.find((plan) => plan.id === selectedPlan);
  };

  const selectedPlanDetails = getSelectedPlanDetails();

  // Handle toggle user block
  const handleToggleBlockStatus = async (user) => {
    // Don't allow blocking admin users
    if (user.role === "ADMIN") {
      alert("Admin users cannot be blocked");
      return;
    }

    // Set loading state for this specific user
    setBlockLoading((prev) => ({ ...prev, [user.id]: true }));

    try {
      const response = await api.users.toggleUserBlock(
        user.id,
        !user.isBlocked
      );

      if (response.success) {
        // Update the user in the local state
        setUsers(
          users.map((u) =>
            u.id === user.id ? { ...u, isBlocked: response.data.isBlocked } : u
          )
        );

        alert(
          `User ${
            response.data.isBlocked ? "blocked" : "unblocked"
          } successfully`
        );
      } else {
        alert(response.message || "Failed to update user block status");
      }
    } catch (error) {
      console.error("Error toggling user block status:", error);
      alert("An error occurred while updating user block status");
    } finally {
      // Clear loading state for this user
      setBlockLoading((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  // Get subscription info to display
  const getSubscriptionInfo = (user) => {
    if (!user.subscription) return "No Subscription";

    if (!user.subscription.isActive) return "Inactive";

    return (
      user.subscription.plan?.displayName ||
      user.subscription.plan?.name ||
      "Unknown"
    );
  };

  // Get subscription badge color
  const getSubscriptionBadgeClass = (user) => {
    if (!user.subscription || !user.subscription.isActive) {
      return "bg-neutral-200 text-neutral-700";
    }

    const planName = user.subscription.plan?.name?.toUpperCase();

    switch (planName) {
      case "FREE":
        return "bg-info-100 text-info-700";
      case "BASIC":
        return "bg-success-100 text-success-700";
      case "PRO":
        return "bg-primary-100 text-primary-700";
      case "ENTERPRISE":
        return "bg-warning-100 text-warning-700";
      default:
        return "bg-neutral-200 text-neutral-700";
    }
  };

  // Filter users based on search term, status, month, and subscription
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === "" ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "Active" && user.isVerified) ||
      (statusFilter === "Inactive" && !user.isVerified) ||
      (statusFilter === "Blocked" && user.isBlocked);

    // Filter by account creation month
    const matchesMonth =
      monthFilter === "" ||
      (user.createdAt &&
        new Date(user.createdAt).getMonth() + 1 === parseInt(monthFilter));

    // Filter by subscription type
    const matchesSubscription =
      subscriptionFilter === "" ||
      user.subscription?.plan?.name === subscriptionFilter;

    return (
      matchesSearch && matchesStatus && matchesMonth && matchesSubscription
    );
  });

  // Calculate pagination
  const indexOfLastUser = currentPage * displayCount;
  const indexOfFirstUser = indexOfLastUser - displayCount;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / displayCount);

  return (
    <>
      <div className="app-content">
        <div className="products-area">
          <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <span className="text-md fw-medium text-secondary-light mb-0">
                  Show
                </span>
                <select
                  className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                  value={displayCount}
                  onChange={(e) => setDisplayCount(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <form
                  className="navbar-search"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <input
                    type="text"
                    className="bg-base h-40-px w-auto"
                    name="search"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Icon icon="ion:search-outline" className="icon" />
                </form>

                {/* Filter for user status */}
                <select
                  className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                  value={statusFilter || ""}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Blocked">Blocked</option>
                </select>

                {/* Filter for account creation month */}
                <select
                  className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                >
                  <option value="">All Months</option>
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>

                {/* Filter for subscription type */}
                <select
                  className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                  value={subscriptionFilter}
                  onChange={(e) => setSubscriptionFilter(e.target.value)}
                >
                  <option value="">All Subscriptions</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.name}>
                      {plan.displayName}
                    </option>
                  ))}
                  <option value="NO_SUBSCRIPTION">No Subscription</option>
                </select>
              </div>
              <Link
                to="/add-user"
                className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
              >
                <Icon
                  icon="ic:baseline-plus"
                  className="icon text-xl line-height-1"
                />
                Add New User
              </Link>
            </div>
            <div className="card-body p-24">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading users...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger text-center" role="alert">
                  {error}
                </div>
              ) : currentUsers.length === 0 ? (
                <div className="text-center py-5">
                  <p>
                    No users found.{" "}
                    {searchTerm && "Try a different search term."}
                  </p>
                </div>
              ) : (
                <div className="row gy-4">
                  {currentUsers.map((user) => (
                    <div
                      key={user.id}
                      className="col-xxl-3 col-md-6 user-grid-card"
                    >
                      <div className="position-relative border radius-16 overflow-hidden">
                        <div className="bg-light p-3">
                          <div className="dropdown position-absolute top-0 end-0 me-16 mt-16">
                            <button
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              className="bg-white-gradient-light w-32-px h-32-px radius-8 border border-light-white d-flex justify-content-center align-items-center text-white"
                            >
                              <Icon
                                icon="entypo:dots-three-vertical"
                                className="icon"
                              />
                            </button>
                            <ul className="dropdown-menu p-12 border bg-base shadow">
                              <li>
                                <Link
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                                  to={`/add-user`}
                                  state={{ userId: user.id, mode: "edit" }}
                                >
                                  Edit
                                </Link>
                              </li>
                              <li>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="delete-btn dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-danger-100 text-hover-danger-600 d-flex align-items-center gap-10"
                                >
                                  Delete
                                </button>
                              </li>
                              {user.role !== "ADMIN" && (
                                <li>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleToggleBlockStatus(user)
                                    }
                                    className={`dropdown-item px-16 py-8 rounded text-secondary-light ${
                                      user.isBlocked
                                        ? "bg-hover-success-100 text-hover-success-600"
                                        : "bg-hover-warning-100 text-hover-warning-600"
                                    } d-flex align-items-center gap-10`}
                                    disabled={blockLoading[user.id]}
                                  >
                                    {blockLoading[user.id] ? (
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                    ) : user.isBlocked ? (
                                      "Unblock User"
                                    ) : (
                                      "Block User"
                                    )}
                                  </button>
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                        <div className="ps-16 pb-16 pe-16 text-center mt-3">
                          {user.avatar ? (
                            <img
                              src={user.avatar}
                              alt={user.name}
                              className="border border-width-2-px w-100-px h-100-px rounded-circle object-fit-cover"
                            />
                          ) : (
                            <div
                              className="border w-100-px h-100-px rounded-circle d-flex justify-content-center align-items-center bg-primary-600 text-white mx-auto"
                              style={{ fontSize: "2rem" }}
                            >
                              {(user.name || user.firstName || user.email || "")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          )}
                          <h6 className="text-lg mb-0 mt-4">
                            {user.name || `${user.firstName} ${user.lastName}`}
                          </h6>
                          <span className="text-secondary-light mb-16">
                            {user.email}
                          </span>
                          <div className="d-flex justify-content-center align-items-center flex-wrap gap-1 mt-2 mb-2">
                            {user.isBlocked && (
                              <span className="badge bg-danger text-white px-2 py-1 me-1">
                                Blocked
                              </span>
                            )}
                            <span
                              className={`badge ${getSubscriptionBadgeClass(
                                user
                              )} px-2 py-1`}
                            >
                              {getSubscriptionInfo(user)}
                            </span>
                          </div>
                          <div className="center-border position-relative bg-danger-gradient-light radius-8 p-12 d-flex align-items-center gap-4">
                            <div className="text-center w-50">
                              <h6 className="text-md mb-0">
                                {user.department || "General"}
                              </h6>
                              <span className="text-secondary-light text-sm mb-0">
                                Department
                              </span>
                            </div>
                            <div className="text-center w-50">
                              <h6 className="text-md mb-0">{user.role}</h6>
                              <span className="text-secondary-light text-sm mb-0">
                                Role
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleViewProfile(user.id)}
                            className="bg-primary-50 text-primary-600 bg-hover-primary-600 hover-text-white p-10 text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center justify-content-center mt-16 fw-medium gap-2 w-100"
                          >
                            View User Details
                            <Icon
                              icon="solar:alt-arrow-right-linear"
                              className="icon text-xl line-height-1"
                            />
                          </button>

                          <button
                            onClick={() => handleBuySubscription(user)}
                            className="bg-success-50 text-success-600 bg-hover-success-600 hover-text-white p-10 text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center justify-content-center mt-8 fw-medium gap-2 w-100"
                          >
                            Buy Subscription
                            <Icon
                              icon="mdi:credit-card-outline"
                              className="icon text-xl line-height-1"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && !error && filteredUsers.length > 0 && (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                  <span>
                    Showing {indexOfFirstUser + 1} to{" "}
                    {Math.min(indexOfLastUser, filteredUsers.length)} of{" "}
                    {filteredUsers.length} entries
                  </span>
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                      >
                        <Icon icon="ep:d-arrow-left" className="" />
                      </button>
                    </li>

                    {[...Array(totalPages).keys()].map((number) => (
                      <li className="page-item" key={number + 1}>
                        <button
                          className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                            currentPage === number + 1
                              ? "bg-primary-600 text-white"
                              : "bg-neutral-200 text-secondary-light"
                          }`}
                          onClick={() => setCurrentPage(number + 1)}
                        >
                          {number + 1}
                        </button>
                      </li>
                    ))}

                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages)
                          )
                        }
                        disabled={currentPage === totalPages}
                      >
                        <Icon icon="ep:d-arrow-right" className="" />
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Purchase Modal */}
      <SubscriptionPurchaseModal
        show={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        user={selectedUser || {}}
        plans={plans}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        onSubmit={handleSubscriptionPurchase}
        loading={subscriptionLoading}
        selectedPlanDetails={selectedPlanDetails}
      />
    </>
  );
};

// Subscription Purchase Modal
const SubscriptionPurchaseModal = ({
  show,
  onClose,
  user,
  plans,
  selectedPlan,
  setSelectedPlan,
  onSubmit,
  loading,
  selectedPlanDetails,
}) => {
  if (!show) return null;

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={!loading ? onClose : undefined}
        style={{ zIndex: 1050 }}
      ></div>

      {/* Modal dialog */}
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1051 }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Buy Subscription for {user.firstName || user.name}{" "}
                {user.lastName || ""}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Select Subscription Plan</label>
                <select
                  className="form-select"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select a plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.displayName || plan.name} (${plan.price} /{" "}
                      {plan.duration} {plan.durationType.toLowerCase()}
                      {plan.duration > 1 ? "s" : ""})
                    </option>
                  ))}
                </select>
              </div>

              {selectedPlanDetails && (
                <div className="alert alert-info">
                  <h6>Plan Details:</h6>
                  <p>
                    <strong>Name:</strong>{" "}
                    {selectedPlanDetails.displayName ||
                      selectedPlanDetails.name}
                  </p>
                  <p>
                    <strong>Price:</strong> ${selectedPlanDetails.price} per{" "}
                    {selectedPlanDetails.duration}{" "}
                    {selectedPlanDetails.durationType.toLowerCase()}
                    {selectedPlanDetails.duration > 1 ? "s" : ""}
                  </p>
                  {selectedPlanDetails.description && (
                    <p>
                      <strong>Description:</strong>{" "}
                      {selectedPlanDetails.description}
                    </p>
                  )}
                  {selectedPlanDetails.features &&
                    selectedPlanDetails.features.length > 0 && (
                      <>
                        <p>
                          <strong>Features:</strong>
                        </p>
                        <ul className="mb-0">
                          {selectedPlanDetails.features.map(
                            (feature, index) => (
                              <li key={index}>{feature}</li>
                            )
                          )}
                        </ul>
                      </>
                    )}
                </div>
              )}

              {user.subscription && (
                <div className="alert alert-warning">
                  <h6>Current Subscription:</h6>
                  <p>
                    This user already has a{" "}
                    {user.subscription.isActive ? "active" : "inactive"}{" "}
                    {user.subscription.plan?.displayName ||
                      user.subscription.plan?.name ||
                      "Unknown"}{" "}
                    plan.
                  </p>
                  <p>
                    Purchasing a new subscription will replace their current
                    one.
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={onSubmit}
                disabled={loading || !selectedPlan}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  "Purchase Subscription"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersGridLayer;
